/**
 * How a multispectral orthophoto is drawn: a vegetation index over a colour ramp, the bands in
 * false colour, or one band in grey. The tiler finds the bands by what the file calls them
 * (docs/tiler-api.md §2.2), so the viewer names roles, never band numbers, and offers only
 * what the tiler's `meta` says the file can give.
 */
import { RDYLGN } from '@/theme/colors';

/** The ratios the tiler computes, each from two bands. */
export const SPECTRAL_INDICES = ['ndvi', 'ndre', 'ngrdi', 'gndvi'] as const;
export type SpectralIndex = (typeof SPECTRAL_INDICES)[number];

/** The bands by what they are; the tiler resolves each to the file's band. */
export const BAND_ROLES = ['blue', 'green', 'red', 'rededge', 'nir'] as const;
export type BandRole = (typeof BAND_ROLES)[number];

export const SPECTRAL_DISPLAYS = [...SPECTRAL_INDICES, 'cir', 'band'] as const;
export type SpectralDisplay = (typeof SPECTRAL_DISPLAYS)[number];

/** Colour infrared: near infrared as red, red as green, green as blue, so foliage reads red. */
export const CIR_BANDS: BandRole[] = ['nir', 'red', 'green'];

/** What the map draws of a multispectral orthophoto; without it, false colour. */
export interface SpectralView {
  display: SpectralDisplay;
  /** The band `band` shows. */
  band?: BandRole;
  /** The index's colour ramp, from its first colour to its last. */
  stretch?: [number, number];
}

export const DEFAULT_SPECTRAL_VIEW: SpectralView = { display: 'cir' };

/** The bands each index divides; an index is offered only when the file has both. */
export const INDEX_BANDS: Record<SpectralIndex, [BandRole, BandRole]> = {
  ndvi: ['nir', 'red'],
  ndre: ['nir', 'rededge'],
  ngrdi: ['green', 'red'],
  gndvi: ['nir', 'green'],
};

/** What the tiler's `meta` says of the bands (absent from a tiler that predates them). */
export interface SpectralMeta {
  band_names?: (string | null)[];
  band_roles?: Partial<Record<BandRole, number>>;
  indices?: string[];
  index_ranges?: Partial<Record<SpectralIndex, [number, number]>>;
}

export const isIndex = (d: SpectralDisplay): d is SpectralIndex => (SPECTRAL_INDICES as readonly string[]).includes(d);

/** The displays a file can give, in the order the page offers them. */
export function availableDisplays(meta: SpectralMeta | undefined): SpectralDisplay[] {
  const roles = meta?.band_roles ?? {};
  const has = (r: BandRole) => roles[r] !== undefined;
  const indices = SPECTRAL_INDICES.filter((i) => (meta?.indices ? meta.indices.includes(i) : INDEX_BANDS[i].every(has)));
  const out: SpectralDisplay[] = [...indices];
  if (CIR_BANDS.every(has)) out.push('cir');
  if (BAND_ROLES.some(has)) out.push('band');
  return out;
}

/** The roles the file has, in spectral order. */
export function availableBands(meta: SpectralMeta | undefined): BandRole[] {
  const roles = meta?.band_roles ?? {};
  return BAND_ROLES.filter((r) => roles[r] !== undefined);
}

const fmt = (v: number) => String(Math.round(v * 1000) / 1000);

/** The tile query that draws `view`, appended to the tile URL as the other kinds' renderings are. */
export function spectralQuery(view: SpectralView | undefined): string {
  const v = view ?? DEFAULT_SPECTRAL_VIEW;
  if (isIndex(v.display)) {
    const stretch = v.stretch ? `&stretch=${fmt(v.stretch[0])},${fmt(v.stretch[1])}` : '';
    return `&index=${v.display}${stretch}&colormap=rdylgn`;
  }
  if (v.display === 'band') return `&bands=${v.band ?? 'nir'}`;
  return `&bands=${CIR_BANDS.join(',')}`;
}

/** The index ramp over a range as the legend draws it: every other colour, six ticks. */
export function indexStops([min, max]: readonly [number, number]): { value: number; color: string }[] {
  const colors = RDYLGN.filter((_, i) => i % 2 === 0);
  const last = colors.length - 1;
  return colors.map((color, i) => ({ value: min + ((max - min) * i) / last, color }));
}
